import { useEffect, useMemo, useRef } from "react";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { Box, CloseButton, Group, Stack } from "@mantine/core";
import { TOP_MENU_ITEMS_MARGINS } from "config/constants";
import { useEditMode } from "hooks/useEditMode";
import { useGenericModals } from "hooks/useGenericModals";
import { useUserTracks } from "hooks/useUserTracks";
import { useUserWaypoints } from "hooks/useUserWaypoints";
import {
  rewriteChartTooltipData,
  setCesiumMode,
} from "stores/genericStore/actions";
import { useGenericDispatch } from "stores/genericStore/GenericContext";
import { rewriteTrackmeta, setIsRouting } from "stores/routingStore/actions";
import { useRouting } from "stores/routingStore/RoutingContext";
import { AddWaypointIcon } from "./DefaultMode/AddWaypointIcon";
import { MeasurementModeIcon } from "./DefaultMode/MeasurementModeIcon";
import { CesiumMapToggleIcon } from "./EditTrackMode/CesiumMapToggleIcon";
import { CopyToClipboardIcon } from "./EditTrackMode/CopyToClipboardIcon";
import { ExportTrackIcon } from "./EditTrackMode/ExportTrackIcon";
import { HotlineIcon } from "./EditTrackMode/HotlineIcon";
import { SaveIndicatorIcon } from "./EditTrackMode/SaveIndicatorIcon";
import { TrackMeta } from "./EditTrackMode/TrackMeta";
import { WaypointMeta } from "./EditWaypointMode/WaypointMeta";
import { DeleteIcon } from "./Shared/DeleteIcon";
import { EditIcon } from "./Shared/EditIcon";
import { FitToScreenIcon } from "./Shared/FitToScreenIcon";
import { GalleryIcon } from "./Shared/GalleryIcon";
import { UndoRedoButtons } from "./UndoRedoButtons";
import { PremiumFeatureWrapper } from "../Wrappers/PremiumFeatureWrapper";
import { useTranslation } from "react-i18next";
import { useAuth } from "hooks/useAuth";

export const TopEditMenuItems = () => {
  const { t } = useTranslation();
  const { openConfirmLosingTrackModal } = useGenericModals();
  const { selectedTrack, trackStatus, selectTrack, leaveOpenTrack } =
    useUserTracks();
  const { selectedWaypoint, selectWaypoint, isAddingWaypoint } =
    useUserWaypoints();
  const { editMode } = useEditMode();
  const genericDispatch = useGenericDispatch();
  const {
    state: { isRouting, trackWaypoints },
    dispatch,
  } = useRouting();
  const selectedTrackRef = useRef(selectedTrack);
  useEffect(() => {
    selectedTrackRef.current = selectedTrack;
  }, [selectedTrack]);
  const computedColorScheme = useShownColorScheme();
  const { user } = useAuth();

  const isTrackEditMode = editMode === "track";
  const isWaypointEditMode = editMode === "waypoint";
  const isDefaultEditMode = editMode === "default";
  const isMeasurementMode = editMode === "measurement";
  const haveSaveableTrackwaypoints = trackWaypoints.length > 1;

  const lockIconStyles = useMemo(
    () => ({
      opacity: 0.8,
      color: computedColorScheme === "dark" ? "#bbb" : "#444",
      border: `1px solid ${
        computedColorScheme === "dark"
          ? "rgba(24, 18, 33, 0.7)"
          : "rgba(238, 238, 238)"
      }`,
      borderRadius: "1px",
      backgroundColor:
        computedColorScheme === "dark"
          ? "rgba(24,18,33, 0.7)"
          : "rgb(238, 238, 238)",
    }),
    [computedColorScheme]
  );

  // A failsafe against an endless loader in ChartPanel when the track is closed while it's being routed.
  // Only once it's closed: while routing, the track can't be saved as a new one yet (and the chart shows the loader)
  const stopRouting = () => {
    if (isRouting) dispatch(setIsRouting(false), false);
  };

  const closeTrack = () => {
    stopRouting();
    dispatch(
      rewriteTrackmeta({
        totalDistance: undefined,
        trackPointsSpeed: undefined,
        trackPointsTime: undefined,
        totalTime: undefined,
      })
    );
    genericDispatch(rewriteChartTooltipData(null));
    selectTrack(null);
  };

  const handleCloseButtonClick = () => {
    if (editMode === "track") {
      if (!selectedTrack && haveSaveableTrackwaypoints) {
        return openConfirmLosingTrackModal({
          onConfirm: () => {
            stopRouting();
            genericDispatch(rewriteChartTooltipData(null));
            dispatch(
              rewriteTrackmeta({
                totalDistance: undefined,
                trackPointsSpeed: undefined,
                trackPointsTime: undefined,
                totalTime: undefined,
              })
            );
            genericDispatch(setCesiumMode(false));
          },
        });
      }

      // An own track asks first when its latest change isn't saved yet
      return void leaveOpenTrack(closeTrack);
    }
    if (editMode === "waypoint") {
      return selectWaypoint(null);
    }
    return undefined;
  };

  return (
    <Stack
      gap={0}
      pt={4}
      style={{
        position: "relative",
        transition: "padding-right 0.3s ease-in-out",
      }}
      pl={6}
      pr={!isDefaultEditMode && !isMeasurementMode ? 18 : 6}
    >
      <Group gap={0} mb={4} wrap="nowrap">
        <UndoRedoButtons isOpen={!isDefaultEditMode && !isMeasurementMode} />

        <AddWaypointIcon
          isOpen={isDefaultEditMode || isMeasurementMode}
          isDisabled={isMeasurementMode}
        />
        <MeasurementModeIcon
          isOpen={isDefaultEditMode || isMeasurementMode}
          isDisabled={isAddingWaypoint}
        />

        <EditIcon isOpen={isTrackEditMode || isWaypointEditMode} />
        <ExportTrackIcon isOpen={isTrackEditMode || isWaypointEditMode} />
        <PremiumFeatureWrapper
          featureName={t("editmode.hotline_button_tooltip")}
          isVisible={isTrackEditMode}
          lockIconStyles={lockIconStyles}
          isDimmed={false}
        >
          <HotlineIcon isOpen={isTrackEditMode} />
        </PremiumFeatureWrapper>
        <FitToScreenIcon isOpen={isTrackEditMode || isWaypointEditMode} />
        <PremiumFeatureWrapper
          featureName={t("editmode.toggle_cesium_map_button_tooltip")}
          isVisible={isTrackEditMode}
          lockIconStyles={lockIconStyles}
          isDimmed={false}
        >
          <CesiumMapToggleIcon isOpen={isTrackEditMode} />
        </PremiumFeatureWrapper>
        <DeleteIcon isOpen={isTrackEditMode || isWaypointEditMode} />
        <CopyToClipboardIcon isOpen={isTrackEditMode} />
        <GalleryIcon
          disabled={
            !user ||
            // This is how we are also checking if the track is not public
            // since unfortunately right now the selected pub track is not
            // being cleared if it's not selected anymore
            (!(isTrackEditMode && selectedTrack && selectedTrack.id !== -1) &&
              !isWaypointEditMode)
          }
          target={
            isTrackEditMode
              ? "track"
              : isWaypointEditMode
                ? "waypoint"
                : undefined
          }
        />
      </Group>

      {!isDefaultEditMode && !isMeasurementMode && (
        <Box
          style={{ position: "relative", width: "100%", height: 32 }}
          mx={TOP_MENU_ITEMS_MARGINS}
        >
          <Box
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
            }}
          >
            <Group
              mb={3}
              style={{ flexWrap: "nowrap", flexShrink: 1, minWidth: 0 }}
              gap={TOP_MENU_ITEMS_MARGINS}
            >
              {isTrackEditMode && (
                <TrackMeta
                  trackName={selectedTrack?.name}
                  status={trackStatus}
                />
              )}
              {isWaypointEditMode && selectedWaypoint && <WaypointMeta />}
              {isTrackEditMode && <SaveIndicatorIcon />}
            </Group>
          </Box>
        </Box>
      )}
      {(isTrackEditMode || isWaypointEditMode) && (
        <CloseButton
          size="sm"
          variant="transparent"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            opacity: isDefaultEditMode ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
          onClick={handleCloseButtonClick}
        />
      )}
    </Stack>
  );
};
